import React, { useEffect, useState } from 'react'

import ApiMonitoringCard from '../../Components/ApiMonitoringCard'
import { Button } from '../../../../components/New/Button/Button'
import DocumentHeader from '../../../../components/Helmet/DocumentHeader'
import { HeaderConstants } from '../../../../constants/HeaderConstants'
import NoDataImage from '../../../../assets/icons/noControlData.svg'
import NoRecord from '../../../../components/common/NoContent/NoRecord'
import NoRecordImage from '../../../../assets/images/folder-not-found 1.svg'
import { Renew } from '@carbon/icons-react'
import Search from '../../../../components/New/Search/Search'
import Spinner from '../../../../components/common/Spinner/Spinner'
import Tooltip from '../../../../components/New/Tooltip/Tooltip'
import { getAPIMonitoringOrganizationOverviewAPI } from '../../../../api/controlCenter/organizationOverview.service'
import { getAllQueryParamsAsObjectFromUrl } from '../../../../utils/UrlUtils'
import { getOrgTimeRangeForLast7DaysEndOfDay } from '../../../../utils/DateTimeUtils'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../global/newPallete'
import { useStoreContext } from '../../../../store/StoreContext'

const CCApiMonitoring = () => {
  const store = useStoreContext()

  const [environmentType, setEnvironmentType] = useState(
    localStorage.getItem('EnvironmentType')
  )
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(localStorage.getItem('activeTab'))

  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchColumn, setSearchColumn] = useState('all')

  const apiData = store.ccApiMonitoringStore.apiOverviewData || []

  const searchedApiData = (
    activeSearchTerm
      ? apiData.filter((item: any) =>
          item.projectName
            ?.toLowerCase()
            .includes(activeSearchTerm.toLowerCase())
        )
      : apiData
  )
    .slice()
    .sort((a: any, b: any) => a.projectName?.localeCompare(b.projectName))

  const fetchData = async () => {
    try {
      setLoading(true)
      store.uiStore.setGlobalLoader(true)
      const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)

      const { startOf7DaysAgo, endOfCurrentDayTimestamp } =
        getOrgTimeRangeForLast7DaysEndOfDay('UTC')

      const paramsWithDateRange = {
        ...allQueryParams,
        'created-from':
          allQueryParams['created-from'] || String(startOf7DaysAgo),
        'created-to':
          allQueryParams['created-to'] || String(endOfCurrentDayTimestamp),
        'time-zone': 'UTC',
        'environment-type': localStorage.getItem('EnvironmentType')
      }

      const response =
        await getAPIMonitoringOrganizationOverviewAPI(paramsWithDateRange)
      store.ccApiMonitoringStore.setApiOverviewData(response.data)
    } catch (err) {
      console.error('Error fetching API overview data:', err)
    } finally {
      store.uiStore.setGlobalLoader(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    const handleStorageChange = () => {
      setEnvironmentType(localStorage.getItem('EnvironmentType'))
      setActiveTab(localStorage.getItem('activeTab'))
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    fetchData()
  }, [environmentType, activeTab])

  return (
    <>
      <DocumentHeader header={HeaderConstants.CONTROL_CENTER_ERRORS} />
      {loading ? (
        <div>
          <Spinner />
        </div>
      ) : (
        <div className='w-full h-full py-[12px]'>
          <div className='flex justify-between items-center mb-[16px]'>
            <div className='w-full relative'>
              <Search
                id='search'
                name='Search'
                placeholder='Search for a project'
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value
                  setSearchTerm(value)
                  setShowDropdown(value.length > 0)
                  if (value.trim() === '') {
                    setActiveSearchTerm('')
                  }
                }}
                autoComplete='off'
                width='50%'
                onPressEnter={() => {
                  setActiveSearchTerm(searchTerm.trim())

                  setShowDropdown(false)
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowDropdown(false)
                  }, 200)
                }}
              />

              {showDropdown && (
                <div
                  style={{ borderColor: pallete.colors.stroke2 }}
                  className='absolute z-[99999] mt-[4px] w-[50%] bg-white border  rounded-md shadow-md'
                >
                  {[{ label: 'Project Name', value: 'ProjectName' }].map(
                    (option) => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setSearchColumn(option.value)
                          setActiveSearchTerm(searchTerm.trim())
                          setShowDropdown(false)
                        }}
                        className='px-[12px] py-[8px] cursor-pointer hover:bg-gray-100 text-sm text-gray-800 flex flex-wrap break-words'
                      >
                        <span className='font-mulish text-black break-words max-w-[100%]'>
                          {searchTerm}
                        </span>
                        <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium whitespace-nowrap'>
                          in: {option.label}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <div className='flex items-center gap-x-[12px]'>
              <Tooltip id='refresh-tooltip' label='Refresh' position='top'>
                <Button
                  id='refresh'
                  type='neutral'
                  startIcon={
                    <Renew size={16} color={pallete.colors.neuButtonText} />
                  }
                  size='small'
                  onClick={fetchData}
                />
              </Tooltip>
            </div>
          </div>
          <div
            className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-[12px] relative'
            style={{ zIndex: 1 }}
          >
            {searchedApiData.length > 0 ? (
              searchedApiData.map((apiItem, index) => (
                <ApiMonitoringCard
                  key={apiItem.projectId || index}
                  projectId={apiItem.projectId}
                  title={apiItem.projectName}
                  projectType={apiItem.projectType}
                  totalApis={apiItem.totalAPIs}
                  failedApis={apiItem.failedAPIs}
                  mostFailedApi={apiItem.mostFailedApiDetails}
                  slowestResponse={apiItem.slowestApiDetails}
                  donutData={[
                    {
                      name: '200 Series',
                      value: Object.values(apiItem.series200).reduce(
                        (sum, v) => sum + v,
                        0
                      ),
                      color: '#4CAF50',
                      details: apiItem.series200
                    },
                    {
                      name: '300 Series',
                      value: Object.values(apiItem.series300).reduce(
                        (sum, v) => sum + v,
                        0
                      ),
                      color: '#2196F3',
                      details: apiItem.series300
                    },
                    {
                      name: '400 Series',
                      value: Object.values(apiItem.series400).reduce(
                        (sum, v) => sum + v,
                        0
                      ),
                      color: '#FF9A9E',
                      details: apiItem.series400
                    },
                    {
                      name: '500 Series',
                      value: Object.values(apiItem.series500).reduce(
                        (sum, v) => sum + v,
                        0
                      ),
                      color: '#F44336',
                      details: apiItem.series500
                    }
                  ]}
                />
              ))
            ) : searchTerm?.trim() ? (
              <div className='col-span-full flex justify-center items-center py-[40px]'>
                <NoRecord
                  imageSrc={NoRecordImage}
                  style={{ height: '45vh' }}
                  text={<div>No API Monitoring match your search.</div>}
                />
              </div>
            ) : (
              <div className='col-span-full flex justify-center items-center py-[40px]'>
                <NoRecord
                  imageSrc={NoDataImage}
                  style={{ height: '45vh' }}
                  text={<div>No data available for the past 7 days. </div>}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default observer(CCApiMonitoring)
